import { Award, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useTranslations } from '@/hooks/use-translations';
import { useCelebrationModal } from '@/modules/celebration-modal';
import celebrationConfig from '@/modules/celebration-modal/config/celebration-modal-config.json';

import { CERTIFICATION_CONFIG } from '../constants';

interface CertificationResultModalProps {
  score: number;
  passed: boolean;
  caseId: string;
  caseTitle: string;
  onClose: () => void;
  remainingAttempts?: number;
}

export const CertificationResultModal = ({
  score,
  passed,
  caseId,
  caseTitle,
  onClose,
  remainingAttempts = 0,
}: CertificationResultModalProps) => {
  const { t } = useTranslations();

  // Déclencher les confettis uniquement si la certification est réussie
  useCelebrationModal(passed, celebrationConfig);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="border-none p-8 shadow-2xl sm:max-w-md"
        hideDefaultClose={!passed}
        onEscapeKeyDown={(e) => !passed && e.preventDefault()}
        onPointerDownOutside={(e) => !passed && e.preventDefault()}
        onInteractOutside={(e) => !passed && e.preventDefault()}
      >
        <div className="text-center">
          {passed ? (
            <>
              <div className="mb-6">
                <Award className="mx-auto h-24 w-24 text-livelinx-green" />
              </div>
              <h2 className="mb-4 font-heading text-3xl text-livelinx-green">
                {t('certification.results.congratulations')}
              </h2>
              <p className="mb-2 font-body text-lg text-livelinx-text">
                {t('certification.results.passed', { caseTitle })}
              </p>
              <p className="mb-6 font-heading text-4xl text-livelinx-purple200">
                {score.toFixed(0)}%
              </p>
              <p className="mb-4 font-body text-sm text-livelinx-grey200">
                {t('certification.results.requiredScore', {
                  passing_score: CERTIFICATION_CONFIG.PASSING_SCORE_CERTIFICATION,
                })}
              </p>
              <div className="mb-6 rounded-lg border border-livelinx-purple200 bg-livelinx-purple50 p-4">
                <p className="font-body text-base text-livelinx-purple200">
                  {t('certification.results.certificateDeliveryMessage')}
                </p>
              </div>
              <Button onClick={onClose} variant="outline" className="w-full">
                {t('common.buttons.close')}
              </Button>
            </>
          ) : (
            <>
              <div className="mb-6">
                <XCircle className="mx-auto h-24 w-24 text-livelinx-red" />
              </div>
              <h2 className="mb-4 font-heading text-3xl text-livelinx-red">
                {t('certification.results.failed')}
              </h2>
              <p className="mb-2 font-body text-lg text-livelinx-text">
                {t('certification.results.yourScore', { caseTitle })}
              </p>
              <p className="mb-6 font-heading text-4xl text-livelinx-purple200">
                {score.toFixed(0)}%
              </p>
              <p className="mb-2 font-body text-sm text-livelinx-grey200">
                {t('certification.results.requiredScore', {
                  passing_score: CERTIFICATION_CONFIG.PASSING_SCORE_CERTIFICATION,
                })}
              </p>
              {remainingAttempts > 0 ? (
                <div className="mb-6 rounded-lg border border-livelinx-blue300 bg-livelinx-blue50 p-4">
                  <p className="font-body text-base text-livelinx-blue300">
                    {t('certification.results.attemptsRemaining', {
                      remaining: remainingAttempts,
                      max: CERTIFICATION_CONFIG.MAX_ATTEMPTS,
                    })}
                  </p>
                  <p className="mt-2 font-body text-sm text-livelinx-grey200">
                    {t('certification.results.retryMessage')}
                  </p>
                </div>
              ) : (
                <div className="mb-6 rounded-lg border border-livelinx-red bg-livelinx-red/10 p-4">
                  <p className="font-body text-base font-semibold text-livelinx-red">
                    {t('certification.results.noAttemptsRemaining')}
                  </p>
                  <p className="mt-2 font-body text-sm text-livelinx-grey200">
                    {t('certification.results.lastAttempt')}
                  </p>
                </div>
              )}
              <Button onClick={onClose} variant="outline" className="w-full">
                {t('common.buttons.close')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
